import { defineSseEvent } from '@terra/shared-sse-contracts';
import { TargetTypeSchema } from '@terra/shared-schemas-orchestrator';
import { z } from 'zod';

export const CRAWLER_SESSION_STARTED_SSE_TYPE =
  'CRAWLER_SESSION_STARTED' as const;
export const CRAWLER_SESSION_STATUS_CHANGED_SSE_TYPE =
  'CRAWLER_SESSION_STATUS_CHANGED' as const;
export const CRAWLER_SESSION_STATS_UPDATED_SSE_TYPE =
  'CRAWLER_SESSION_STATS_UPDATED' as const;

export const CrawlerSessionStatusSchema = z.enum([
  'RUNNING',
  'ABORTING',
  'COMPLETED',
  'FAILED',
  'ABORTED',
]);

export type CrawlerSessionStatus = z.infer<typeof CrawlerSessionStatusSchema>;

export const CrawlerSessionStartedSsePayloadSchema = z.object({
  id: z.string(),
  targetId: z.string(),
  targetType: TargetTypeSchema,
  credentialId: z.string().nullable(),
  userId: z.string().nullable(),
  parentId: z.string().nullable(),
  crawlerType: z.enum([
    'SHALLOW',
    'AI',
    'MANUAL',
    'AUTHENTICATION',
    'FEATURE_ANALYSIS',
    'NAVIGATION_PROFILE',
  ]),
  status: CrawlerSessionStatusSchema,
  createdAt: z.string(),
  lastHeartbeatAt: z.string(),
});

export const CrawlerSessionStatusChangedSsePayloadSchema = z.object({
  sessionId: z.string(),
  targetId: z.string(),
  targetType: TargetTypeSchema,
  credentialId: z.string().nullable(),
  status: CrawlerSessionStatusSchema,
  finishedAt: z.string().nullable().optional(),
  error: z.string().nullable().optional(),
});

export const CrawlerSessionStatsUpdatedSsePayloadSchema = z.object({
  sessionId: z.string(),
  targetId: z.string(),
  targetType: TargetTypeSchema,
  credentialId: z.string().nullable(),
  stats: z.object({
    crawlerSessionId: z.string(),
    harEntriesNew: z.number(),
    harEntriesExisting: z.number(),
    endpointsNew: z.number(),
    endpointsExisting: z.number(),
    pagesNew: z.number(),
    pagesExisting: z.number(),
    webpagesNew: z.number(),
    webpagesExisting: z.number(),
    updatedAt: z.string(),
  }),
});

const getCrawlerChannelKey = ({
  targetId,
  credentialId,
}: {
  targetId: string;
  credentialId: string | null;
}): string => `${targetId}:${credentialId ?? 'anonymous'}`;

export const CRAWLER_SESSION_STARTED_SSE_EVENT = defineSseEvent({
  type: CRAWLER_SESSION_STARTED_SSE_TYPE,
  payload: CrawlerSessionStartedSsePayloadSchema,
  channelPrefix: 'crawler',
  channelKey: getCrawlerChannelKey,
  target: {
    type: (payload) => payload.targetType,
    id: (payload) => payload.targetId,
  },
});

export const CRAWLER_SESSION_STATUS_CHANGED_SSE_EVENT = defineSseEvent({
  type: CRAWLER_SESSION_STATUS_CHANGED_SSE_TYPE,
  payload: CrawlerSessionStatusChangedSsePayloadSchema,
  channelPrefix: 'crawler',
  channelKey: getCrawlerChannelKey,
  target: {
    type: (payload) => payload.targetType,
    id: (payload) => payload.targetId,
  },
});

export const CRAWLER_SESSION_STATS_UPDATED_SSE_EVENT = defineSseEvent({
  type: CRAWLER_SESSION_STATS_UPDATED_SSE_TYPE,
  payload: CrawlerSessionStatsUpdatedSsePayloadSchema,
  channelPrefix: 'crawler',
  channelKey: getCrawlerChannelKey,
  target: {
    type: (payload) => payload.targetType,
    id: (payload) => payload.targetId,
  },
});

export type CrawlerSessionStartedSsePayload = z.infer<
  typeof CrawlerSessionStartedSsePayloadSchema
>;
export type CrawlerSessionStatusChangedSsePayload = z.infer<
  typeof CrawlerSessionStatusChangedSsePayloadSchema
>;
export type CrawlerSessionStatsUpdatedSsePayload = z.infer<
  typeof CrawlerSessionStatsUpdatedSsePayloadSchema
>;
